import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import TableHeader from '../../common/TableHeader'
import { useParams, useSearchParams } from 'react-router-dom'
import api from '../../../service/api'
import { formatDate, Icons } from '../../../utils/utilities'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import errorImg from '../../../assets/request-error.svg'
import noExamImg from '../../../assets/noexams.svg'
import AuditExamViewer from '../exams/AuditExamViewer'
import { Analytics, OpenInNew } from '@mui/icons-material'
import AuditExamParticipations from '../exams/AuditExamParticipations'
import CustomTable from '../../common/custom-table/CustomTable'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'

const ExamDetails = ( { userData } ) => {

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [examDetails, setExamDetails] = useState( [] )
    const [selectedExam, setSelectedExam] = useState( null )
    const [partipationsViewerState, setPartipationsViewerState] = useState( false )

    const { empcode } = useParams()
    const [searchParams] = useSearchParams( { legacy: true } )
    const legacy = searchParams.get( 'legacy' ) === 'true'

    const { palette, layout } = useTheme()

    const columns = useMemo( () => [
        { field: "index", index: true, name: "Sl. no.", align: "center" },
        { field: "exam_name", name: "Exam Title" },
        { field: "created_at", renderCell: item => formatDate( item.created_at ), name: "Created on" },
        { field: "subject", name: "Subject", renderCell: item => item?.subject?.subject_name || "Not Available" },
        { field: "attempts", name: "Attempts", align: "center" },
        {
            field: "actions", name: "Actions", align: "right", renderCell: item => ( <Box display="flex" justifyContent="flex-end" gap="10px" alignItems="center">
                <Tooltip title="Open details">
                    <IconButton onClick={() => setSelectedExam( item )} color="primary" size="small"><OpenInNew fontSize='small' /></IconButton>
                </Tooltip>
                {+item.attempts > 0 && <Tooltip title="View participations">
                    <IconButton onClick={() => { setSelectedExam( item ); setPartipationsViewerState( true ) }} color="secondary" size="small"><Analytics fontSize='small' /></IconButton>
                </Tooltip>}
            </Box> )
        },
    ], [] )

    const handleDialogClose = () => {
        setSelectedExam( null )
    }

    const loadExams = useCallback( async () => {
        setLoading( true )
        try {
            const { data: { data } } = await api.audit.getExamsByFacultyId( empcode, { legacy } )
            setExamDetails( data )
            setError( false )
        } catch ( err ) {
            setError( err?.response?.data?.message || "Something went wrong while fetching assignments details." )
        } finally {
            setLoading( false )
        }
    }, [empcode, legacy] )

    useEffect( () => {
        loadExams()
    }, [loadExams] )

    return (
        <Box maxHeight="350px" display="flex" flexDirection="column" overflow="hidden">

            {selectedExam && <AuditExamViewer setExamList={setExamDetails} exam={selectedExam} handleClose={handleDialogClose} openState={Boolean( selectedExam ) && !partipationsViewerState} />}
            {selectedExam && partipationsViewerState && <AuditExamParticipations exam={selectedExam} handleClose={() => { setPartipationsViewerState( false ); setSelectedExam( null ) }} openState={partipationsViewerState} />}

            {/* Data */}
            {!loading && !error && examDetails.length > 0 && <CustomTable columns={columns} data={examDetails} />}

            {/* Loading */}
            {loading && !error && <Box padding="50px 20px" display="flex" justifyContent="center">
                <DataLoadingSpinner waitingMessage="Please wait! Loading exams..." />
            </Box>}

            {/* Error */}
            {error && <TryAgainMessageBlock getData={loadExams} loading={loading} err={error} />}

            {/* No Data */}
            {!loading && !error && examDetails.length === 0 && <Box borderRadius={layout.borderRadius} bgcolor={palette.contentBg} padding="20px" gap="20px" display="flex" alignItems="center" flexDirection="column" justifyContent="center">
                <img src={noExamImg} height="100px" alt="Something went wrong!" />
                <Typography variant="subtitle2"><em>{userData.employee_name}</em> hasn't given any exams!</Typography>
                <Button disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay} onClick={loadExams} variant="outlined" sx={{ textTransform: "capitalize" }} color="secondary">Fetch again</Button>
            </Box>}
        </Box>
    )
}

export default ExamDetails
